/**
 * Image types most browsers cannot draw in an `<img>`: phone HEIC/HEIF
 * photos and TIFF scans. A thumbnail built from one is a broken icon, so
 * these show as a file row with a download instead.
 *
 * A denylist, not an allowlist: stored types include header-supplied
 * variants (image/jpg, image/pjpeg) and imported formats (BMP, AVIF, SVG)
 * that browsers draw fine. The importer's no-preview caveat uses the same
 * set (`_UNDRAWABLE_IMAGE_MIME` in backend kinnser_import/wounds.py); keep
 * the two in step.
 */
const BROWSER_UNDRAWABLE_IMAGE_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/tiff',
]);

const baseMimeType = (mimeType: string | null | undefined): string =>
  mimeType?.split(';')[0].trim().toLowerCase() ?? '';

export const isBrowserRenderableImage = (
  mimeType: string | null | undefined
): boolean => {
  const base = baseMimeType(mimeType);
  return base.startsWith('image/') && !BROWSER_UNDRAWABLE_IMAGE_TYPES.has(base);
};

/** A photo the browser cannot draw, so it is offered as a download only. */
export const isDownloadOnlyImage = (
  mimeType: string | null | undefined
): boolean => BROWSER_UNDRAWABLE_IMAGE_TYPES.has(baseMimeType(mimeType));
